import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";

/**
 * Role-mapping grid (reference "Role Mapping" / "Access & Roles" step):
 * each tenant role maps to a per-portal access level. During public
 * signup this captures intent only — real role→portal assignment needs
 * provisioned user ids, so it is persisted as featureFlags.roleMapping and
 * applied in AdminRoles post-login against tis-auth-rbac-service (3004).
 */
const DEFAULT_ROLES = ["Administrator", "Recruiter", "Hiring Manager", "Candidate"];
const ACCESS_LEVELS = [
  { id: "full", label: "Full access" },
  { id: "limited", label: "Limited access" },
  { id: "view", label: "View only" },
  { id: "none", label: "No access" },
];

const DEFAULT_MAPPING = {
  Administrator: { talent: "full", hub: "full" },
  Recruiter: { talent: "limited", hub: "full" },
  "Hiring Manager": { talent: "view", hub: "limited" },
  Candidate: { talent: "full", hub: "none" },
};

export default function RoleMappingFields({ featureConfig, onChange, portals = ["talent", "hub"] }) {
  const mapping = featureConfig.roleMapping || DEFAULT_MAPPING;
  const setAccess = (role, portal, level) =>
    onChange({
      ...featureConfig,
      roleMapping: { ...mapping, [role]: { ...(mapping[role] || {}), [portal]: level } },
    });

  return (
    <Card data-testid="role-mapping">
      <CardHeader>
        <CardTitle className="text-base">Role Mapping</CardTitle>
        <CardDescription>Map your tenant roles to portal access.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-2 py-2 font-medium">Tenant role</th>
                {portals.map((p) => (
                  <th key={p} className="px-2 py-2 font-medium capitalize">{p} Portal access</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DEFAULT_ROLES.map((role) => (
                <tr key={role} className="border-b last:border-0">
                  <td className="px-2 py-2 font-medium">{role}</td>
                  {portals.map((portal) => (
                    <td key={portal} className="px-2 py-2">
                      <Select
                        value={(mapping[role] || {})[portal] || "none"}
                        onValueChange={(v) => setAccess(role, portal, v)}
                      >
                        <SelectTrigger className="h-8 w-40" data-testid={`role-${role.replace(/\s+/g, "-").toLowerCase()}-${portal}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCESS_LEVELS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sr-only"><Label>Role mapping grid</Label></div>
      </CardContent>
    </Card>
  );
}

export { DEFAULT_MAPPING };
